import React, { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { Popover } from '@material-ui/core'
import { ChromePicker } from 'react-color'
import { Panel } from 'src/components/app-ui'
import useDebouncedCallback from 'src/hooks/useDebouncedCallback'
import { DEFAULT_FONT, DEFAULT_PRIMARY_COLOR } from './useBranding'
import FontSelect from './font-select'
import useStyles from './styles'

// The branding set: mid-light, saturated colors, so their tints don't turn
// grey in the default covers and white text and buttons read on them.
const SWATCHES = [
  '#015FFB', // Default blue
  '#4F46E5', // Indigo
  '#0E7C86', // Petrol
  '#15803D', // Green
  '#D9480F', // Orange
  '#E11D48', // Rose
  '#9333EA', // Purple
]

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

const normalizeHex = value => {
  const hex = value.trim().startsWith('#') ? value.trim() : `#${value.trim()}`
  return HEX.test(hex) ? hex.toUpperCase() : null
}

/**
 * Primary color (presets, picker and hex field) and font. Changes save
 * automatically; picker drags are debounced.
 */
const ColorsCard = ({ branding, onSave }) => {
  const classes = useStyles()
  const savedColor = (
    branding.primaryColor || DEFAULT_PRIMARY_COLOR
  ).toUpperCase()
  const [color, setColor] = useState(savedColor)
  const [hexText, setHexText] = useState(savedColor)
  const [pickerAnchor, setPickerAnchor] = useState(null)

  useEffect(() => {
    setColor(savedColor)
    setHexText(savedColor)
  }, [savedColor])

  const saveColor = next => {
    if (next !== savedColor) onSave({ primaryColor: next })
  }
  const saveColorDebounced = useDebouncedCallback(saveColor, 500)

  const pick = next => {
    setColor(next)
    setHexText(next)
    saveColor(next)
  }

  const commitHex = () => {
    const next = normalizeHex(hexText)
    if (next) pick(next)
    else setHexText(color)
  }

  return (
    <Panel className={classes.card}>
      <div className={classes.cardTitle}>Colors</div>
      <div className={classes.field}>
        Primary color
        <div className={classes.swatches}>
          {SWATCHES.map(swatch => (
            <button
              key={swatch}
              type="button"
              title={swatch}
              className={classes.swatch}
              style={{
                background: swatch,
                boxShadow:
                  swatch === color
                    ? `0 0 0 2px #FFFFFF, 0 0 0 4px ${swatch}`
                    : 'none',
              }}
              onClick={() => pick(swatch)}
            />
          ))}
          <button
            type="button"
            title="Custom color"
            className={classes.picker}
            style={
              SWATCHES.includes(color)
                ? undefined
                : {
                    background: color,
                    border: 0,
                    boxShadow: `0 0 0 2px #FFFFFF, 0 0 0 4px ${color}`,
                  }
            }
            onClick={event => setPickerAnchor(event.currentTarget)}
          >
            {SWATCHES.includes(color) && (
              <Icon icon="tabler:color-picker" width={16} height={16} />
            )}
          </button>
          <label className={classes.hex}>
            <span className={classes.hexDot} style={{ background: color }} />
            <input
              className={classes.hexInput}
              value={hexText}
              maxLength={7}
              spellCheck={false}
              onChange={event => setHexText(event.target.value)}
              onBlur={commitHex}
              onKeyDown={event => event.key === 'Enter' && event.target.blur()}
            />
          </label>
        </div>
        <Popover
          open={Boolean(pickerAnchor)}
          anchorEl={pickerAnchor}
          onClose={() => setPickerAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <ChromePicker
            color={color}
            disableAlpha
            onChange={value => {
              const next = value.hex.toUpperCase()
              setColor(next)
              setHexText(next)
              saveColorDebounced(next)
            }}
          />
        </Popover>
      </div>
      <div className={classes.field}>
        Font
        <FontSelect
          value={branding.font || DEFAULT_FONT}
          onChange={family => onSave({ font: family })}
        />
      </div>
    </Panel>
  )
}

export default ColorsCard
